<template>
  <div>
    <!-- MYAC-1522 (decision C): no QMS nor product, no assessment to be told
         about — the section says nothing useful (« Aucun projet accessible »). -->
    <template v-if="isLoading || assessmentProjectsByTenant.length > 0">
    <!-- Assessment Completed Notifications Section -->
    <div class="mb-8">
      <h3 class="text-lg font-semibold mb-2">{{ t('profile.notifications.assessment_completed.title') }}</h3>
      <p class="text-color-secondary text-sm mb-4">{{ t('profile.notifications.assessment_completed.description') }}</p>

      <div v-if="isLoading" class="flex items-center gap-2 py-4">
        <i class="pi pi-spin pi-spinner"></i>
        <span class="text-color-secondary">{{ t('common.loading') }}</span>
      </div>

      <div v-else-if="assessmentProjectsByTenant.length === 0" class="py-4">
        <p class="text-color-secondary">{{ t('profile.notifications.no_projects') }}</p>
      </div>

      <div v-else class="flex flex-col gap-6">
        <div
          v-for="group in assessmentProjectsByTenant"
          :key="group.tenantId"
          class="flex flex-col gap-3"
        >
          <h4 class="text-base font-semibold text-color-secondary">{{ group.tenantName }}</h4>
          <div class="flex flex-col gap-1 pl-4">
            <div
              v-for="project in group.projects"
              :key="project.id"
              class="flex items-center gap-3 py-1"
            >
              <ToggleSwitch
                :modelValue="isAssessmentProjectEnabled(project.id)"
                @update:modelValue="toggleAssessmentProject(project.id, $event)"
              />
              <span class="font-medium">{{ project.name }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <Divider />
    </template>

    <!-- Regulation Watch Notifications Section -->
    <div class="mb-8 mt-6">
      <h3 class="text-lg font-semibold mb-2">{{ t('profile.notifications.regulation_watch.title') }}</h3>
      <p class="text-color-secondary text-sm mb-4">{{ t('profile.notifications.regulation_watch.description') }}</p>

      <div v-if="isLoading" class="flex items-center gap-2 py-4">
        <i class="pi pi-spin pi-spinner"></i>
        <span class="text-color-secondary">{{ t('common.loading') }}</span>
      </div>

      <div v-else-if="regulationWatchProjectsByTenant.length === 0" class="py-4">
        <p class="text-color-secondary">{{ t('profile.notifications.no_projects') }}</p>
      </div>

      <div v-else class="flex flex-col gap-6">
        <div
          v-for="group in regulationWatchProjectsByTenant"
          :key="group.tenantId"
          class="flex flex-col gap-3"
        >
          <h4 class="text-base font-semibold text-color-secondary">{{ group.tenantName }}</h4>
          <div class="flex flex-col gap-1 pl-4">
            <div
              v-for="project in group.projects"
              :key="project.id"
              class="flex items-center gap-3 py-1"
            >
              <ToggleSwitch
                :modelValue="isRegWatchProjectEnabled(project.id)"
                @update:modelValue="toggleRegWatchProject(project.id, $event)"
              />
              <span class="font-medium">{{ project.name }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'
import ToggleSwitch from 'primevue/toggleswitch'
import Divider from 'primevue/divider'
import { useProjectsStore } from '@/stores/projects'
import { useTenantsStore } from '@/stores/tenants'
import type { Project } from '@/models/Project'

const props = defineProps<{
  assessmentProjects: string[]
  regulationWatchProjects: string[]
}>()

const emit = defineEmits<{
  'update:assessmentProjects': [value: string[]]
  'update:regulationWatchProjects': [value: string[]]
}>()

const { t } = useI18n()

const projectsStore = useProjectsStore()
const tenantsStore = useTenantsStore()

const { projects } = storeToRefs(projectsStore)
const { tenants } = storeToRefs(tenantsStore)

const isLoading = ref(true)

interface ProjectGroup {
  tenantId: string
  tenantName: string
  projects: Project[]
}

function groupByTenant(filteredProjects: Project[]): ProjectGroup[] {
  if (!filteredProjects.length || !tenants.value.length) return []

  const tenantNameById = new Map<string, string>()
  tenants.value.forEach((tenant) => {
    tenantNameById.set(tenant.id, tenant.name)
  })

  const groupsMap = new Map<string, ProjectGroup>()

  filteredProjects.forEach((project) => {
    const tenantId = project.tenant?.id
    if (!tenantId) return

    const tenantName = tenantNameById.get(tenantId) || 'Unknown'
    if (!groupsMap.has(tenantId)) {
      groupsMap.set(tenantId, { tenantId, tenantName, projects: [] })
    }
    groupsMap.get(tenantId)!.projects.push(project)
  })

  return Array.from(groupsMap.values())
    .map((group) => ({
      ...group,
      projects: group.projects.sort((a, b) => a.name.localeCompare(b.name)),
    }))
    .sort((a, b) => a.tenantName.localeCompare(b.tenantName))
}

const assessmentProjectsByTenant = computed(() =>
  groupByTenant(projects.value.filter(p => p.type !== 'regulatory_monitoring'))
)

const regulationWatchProjectsByTenant = computed(() =>
  groupByTenant(projects.value.filter(p => p.type === 'regulatory_monitoring'))
)

function isAssessmentProjectEnabled(projectId: string): boolean {
  return props.assessmentProjects.includes(projectId)
}

function toggleAssessmentProject(projectId: string, enabled: boolean) {
  const current = [...props.assessmentProjects]
  if (enabled) {
    if (!current.includes(projectId)) current.push(projectId)
  } else {
    const idx = current.indexOf(projectId)
    if (idx >= 0) current.splice(idx, 1)
  }
  emit('update:assessmentProjects', current)
}

function isRegWatchProjectEnabled(projectId: string): boolean {
  return props.regulationWatchProjects.includes(projectId)
}

function toggleRegWatchProject(projectId: string, enabled: boolean) {
  const current = [...props.regulationWatchProjects]
  if (enabled) {
    if (!current.includes(projectId)) current.push(projectId)
  } else {
    const idx = current.indexOf(projectId)
    if (idx >= 0) current.splice(idx, 1)
  }
  emit('update:regulationWatchProjects', current)
}

onMounted(async () => {
  isLoading.value = true
  try {
    await Promise.all([
      projectsStore.listProjects(),
      tenantsStore.listTenants()
    ])
  } finally {
    isLoading.value = false
  }
})
</script>
